/**
 * OperationTemplateEditor.js
 *
 * Slide-up modal for editing operation templates.
 * Allows editing operation name, fields (with types), and checks.
 * Phase 3 of the Custom Operation Types feature.
 */

import React, { useState, useEffect } from 'react';
import { X, Save, Plus, Trash2, GripVertical, ChevronDown, ChevronUp, ToggleLeft, ToggleRight } from 'lucide-react';

// Field type options
const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'time', label: 'Time' },
  { value: 'select', label: 'Dropdown' },
  { value: 'radio', label: 'Radio Buttons' }
];

// Generate unique ID
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const OperationTemplateEditor = ({
  isOpen,
  onClose,
  operation,
  onSave
}) => {
  // Local state for editing
  const [label, setLabel] = useState('');
  const [fields, setFields] = useState([]);
  const [checks, setChecks] = useState([]);
  const [expandedField, setExpandedField] = useState(null);

  // Initialize from operation prop
  useEffect(() => {
    if (operation) {
      setLabel(operation.label || '');
      setFields(operation.fields || []);
      setChecks(operation.checks || []);
    }
  }, [operation]);

  if (!isOpen) return null;

  // Field handlers
  const addField = () => {
    const newField = {
      id: generateId(),
      label: '',
      type: 'text',
      required: false,
      options: []
    };
    setFields([...fields, newField]);
    setExpandedField(newField.id);
  };

  const updateField = (fieldId, updates) => {
    setFields(fields.map(f =>
      f.id === fieldId ? { ...f, ...updates } : f
    ));
  };

  const removeField = (fieldId) => {
    setFields(fields.filter(f => f.id !== fieldId));
    if (expandedField === fieldId) setExpandedField(null);
  };

  const addFieldOption = (fieldId) => {
    setFields(fields.map(f => {
      if (f.id === fieldId) {
        return {
          ...f,
          options: [...(f.options || []), { value: '', label: '' }]
        };
      }
      return f;
    }));
  };

  const updateFieldOption = (fieldId, optionIndex, updates) => {
    setFields(fields.map(f => {
      if (f.id === fieldId) {
        const newOptions = [...(f.options || [])];
        newOptions[optionIndex] = { ...newOptions[optionIndex], ...updates };
        return { ...f, options: newOptions };
      }
      return f;
    }));
  };

  const removeFieldOption = (fieldId, optionIndex) => {
    setFields(fields.map(f => {
      if (f.id === fieldId) {
        const newOptions = [...(f.options || [])];
        newOptions.splice(optionIndex, 1);
        return { ...f, options: newOptions };
      }
      return f;
    }));
  };

  // Check handlers
  const addCheck = () => {
    const newCheck = {
      id: generateId(),
      text: '',
      required: true
    };
    setChecks([...checks, newCheck]);
  };

  const updateCheck = (checkId, updates) => {
    setChecks(checks.map(c =>
      c.id === checkId ? { ...c, ...updates } : c
    ));
  };

  const removeCheck = (checkId) => {
    setChecks(checks.filter(c => c.id !== checkId));
  };

  // Save handler
  const handleSave = () => {
    // Clean up empty fields and checks
    const cleanedFields = fields.filter(f => f.label.trim());
    const cleanedChecks = checks.filter(c => c.text.trim());

    // Clean up field options
    const finalFields = cleanedFields.map(f => {
      if (f.type === 'select' || f.type === 'radio') {
        return {
          ...f,
          options: (f.options || []).filter(o => o.value.trim() || o.label.trim())
        };
      }
      return f;
    });

    onSave({
      ...operation,
      label: label.trim() || operation.label,
      fields: finalFields,
      checks: cleanedChecks
    });
    onClose();
  };

  const needsOptions = (type) => type === 'select' || type === 'radio';

  return (
    <>
      {/* Backdrop - z-[60] to appear above VoyageSettingsModal */}
      <div
        className="fixed inset-0 bg-black/40 z-[60]"
        onClick={onClose}
      />

      {/* Slide-up Panel - z-[60] to appear above VoyageSettingsModal */}
      <div className="fixed bottom-0 left-0 right-0 z-[60] animate-slide-up">
        <div className="bg-white rounded-t-2xl shadow-xl border-t border-slate-200 max-w-lg mx-auto safe-area-bottom max-h-[85vh] flex flex-col">
          {/* Handle bar */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-10 h-1 bg-slate-300 rounded-full"></div>
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-5 pb-3 border-b border-slate-100">
            <h3 className="font-semibold text-slate-900">Edit Operation Template</h3>
            <button
              onClick={onClose}
              className="p-2 -mr-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {/* Operation Name */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Operation Name
              </label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g., Pilot Transfer"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
            </div>

            {/* Fields Section */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-slate-700">
                  Data Fields
                </label>
                <span className="text-xs text-slate-500">
                  {fields.length} field{fields.length !== 1 ? 's' : ''}
                </span>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Fields capture data when starting the operation (e.g., passenger count, cargo type)
              </p>

              {fields.length === 0 ? (
                <p className="text-sm text-slate-400 italic py-2">No fields configured</p>
              ) : (
                <div className="space-y-2 mb-3">
                  {fields.map((field) => (
                    <div key={field.id} className="border border-slate-200 rounded-lg overflow-hidden">
                      {/* Field Header Row */}
                      <div
                        className="flex items-center gap-2 px-3 py-2 bg-slate-50 cursor-pointer"
                        onClick={() => setExpandedField(expandedField === field.id ? null : field.id)}
                      >
                        <GripVertical className="h-4 w-4 text-slate-400 flex-shrink-0" />
                        <span className="text-sm text-slate-700 flex-1 truncate">
                          {field.label || 'Untitled field'}
                        </span>
                        <span className="text-xs text-slate-500 px-2 py-0.5 bg-slate-200 rounded">
                          {FIELD_TYPES.find(t => t.value === field.type)?.label || field.type}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); removeField(field.id); }}
                          className="text-slate-400 hover:text-red-500 p-1"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        {expandedField === field.id ? (
                          <ChevronUp className="h-4 w-4 text-slate-400" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-slate-400" />
                        )}
                      </div>

                      {/* Expanded Field Editor */}
                      {expandedField === field.id && (
                        <div className="px-3 py-3 space-y-3 border-t border-slate-200">
                          {/* Field Label */}
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Label</label>
                            <input
                              type="text"
                              value={field.label}
                              onChange={(e) => updateField(field.id, { label: e.target.value })}
                              placeholder="Field label..."
                              className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-cyan-500"
                            />
                          </div>

                          {/* Field Type */}
                          <div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Type</label>
                            <select
                              value={field.type}
                              onChange={(e) => updateField(field.id, { type: e.target.value })}
                              className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-cyan-500"
                            >
                              {FIELD_TYPES.map(type => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                              ))}
                            </select>
                          </div>

                          {/* Required Toggle */}
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-medium text-slate-600">Required</label>
                            <button
                              onClick={() => updateField(field.id, { required: !field.required })}
                              className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                                field.required
                                  ? 'bg-cyan-100 text-cyan-700'
                                  : 'bg-slate-100 text-slate-500'
                              }`}
                            >
                              {field.required ? (
                                <><ToggleRight className="h-4 w-4" /> Yes</>
                              ) : (
                                <><ToggleLeft className="h-4 w-4" /> No</>
                              )}
                            </button>
                          </div>

                          {/* Options for select/radio types */}
                          {needsOptions(field.type) && (
                            <div>
                              <label className="block text-xs font-medium text-slate-600 mb-1">Options</label>
                              {(field.options || []).length === 0 ? (
                                <p className="text-xs text-slate-400 italic">No options added</p>
                              ) : (
                                <div className="space-y-1 mb-2">
                                  {(field.options || []).map((option, idx) => (
                                    <div key={idx} className="flex items-center gap-2">
                                      <input
                                        type="text"
                                        value={option.label}
                                        onChange={(e) => updateFieldOption(field.id, idx, {
                                          label: e.target.value,
                                          value: e.target.value.toLowerCase().replace(/\s+/g, '_')
                                        })}
                                        placeholder="Option label"
                                        className="flex-1 px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-cyan-500"
                                      />
                                      <button
                                        onClick={() => removeFieldOption(field.id, idx)}
                                        className="text-slate-400 hover:text-red-500 p-1"
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <button
                                onClick={() => addFieldOption(field.id)}
                                className="text-xs text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
                              >
                                <Plus className="h-3 w-3" /> Add option
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={addField}
                className="w-full py-2 text-sm text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 rounded-lg border border-dashed border-cyan-300 flex items-center justify-center gap-2"
              >
                <Plus className="h-4 w-4" /> Add Field
              </button>
            </div>

            {/* Checks Section */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-slate-700">
                  Pre-Operation Checks
                </label>
                <span className="text-xs text-slate-500">
                  {checks.length} check{checks.length !== 1 ? 's' : ''}
                </span>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Checks must be completed before the operation can start
              </p>

              {checks.length === 0 ? (
                <p className="text-sm text-slate-400 italic py-2">No checks configured</p>
              ) : (
                <div className="space-y-2 mb-3">
                  {checks.map((check) => (
                    <div key={check.id} className="flex items-start gap-2 bg-slate-50 rounded-lg px-3 py-2">
                      <GripVertical className="h-4 w-4 text-slate-400 flex-shrink-0 mt-1" />
                      <input
                        type="text"
                        value={check.text}
                        onChange={(e) => updateCheck(check.id, { text: e.target.value })}
                        placeholder="Check description..."
                        className="flex-1 px-2 py-1 text-sm border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-cyan-500"
                      />
                      <button
                        onClick={() => updateCheck(check.id, { required: !check.required })}
                        className={`px-2 py-1 rounded text-xs flex-shrink-0 ${
                          check.required
                            ? 'bg-cyan-100 text-cyan-700'
                            : 'bg-slate-200 text-slate-500'
                        }`}
                        title={check.required ? 'Required' : 'Optional'}
                      >
                        {check.required ? 'Req' : 'Opt'}
                      </button>
                      <button
                        onClick={() => removeCheck(check.id)}
                        className="text-slate-400 hover:text-red-500 p-1 flex-shrink-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={addCheck}
                className="w-full py-2 text-sm text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 rounded-lg border border-dashed border-cyan-300 flex items-center justify-center gap-2"
              >
                <Plus className="h-4 w-4" /> Add Check
              </button>
            </div>
          </div>

          {/* Footer with Save/Cancel */}
          <div className="px-5 py-4 border-t border-slate-100 bg-slate-50">
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-2.5 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex-1 py-2.5 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 flex items-center justify-center gap-2"
              >
                <Save className="h-4 w-4" /> Save Template
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OperationTemplateEditor;
